import {
  Button,
  Center,
  FiPlus,
  GridItem,
  useFieldArray,
  useFormContext,
} from '@agrotoken-tech/ui';
import React, { useEffect } from 'react';
import { DocumentSection } from '../DocumentSection';

interface Props {
  name: string;
  isDocumentRequired?: boolean;
  label: string;
  handleRemove?: () => void;
  additionalLabel: string;
  hideDelete?: boolean;
  mt?: Parameters<typeof GridItem>[0]['mt'];
  shouldHideInitialField?: boolean;
  isReadonly?: boolean;
  helperText?: string;
  defaultReadOnlyName?: string;
}

const defaultValueShape = [
  { file: { mimeType: undefined, s3TempKey: undefined, type: undefined } },
];

export const MultipleDocumentSection = ({
  name,
  isDocumentRequired = false,
  label,
  additionalLabel,
  hideDelete = false,
  mt,
  shouldHideInitialField = false,
  isReadonly = false,
  helperText,
  defaultReadOnlyName,
}: Props) => {
  const {
    setValue,
    watch,
    unregister,
    control,
    formState: { isSubmitted },
  } = useFormContext();
  const {
    fields: fieldsArray,
    append: appendField,
    remove: removeField,
  } = useFieldArray({
    control: control,
    name,
  });

  useEffect(() => {
    // set default values in case there's no values
    const actualValues = watch(name) || [];
    const hasValues = (obj: object) =>
      Object.values(obj).filter((e) => e !== undefined).length > 1;
    const elementsWithData = actualValues
      .map((element: object) => hasValues(element))
      .filter(Boolean);

    if (!elementsWithData.length && !shouldHideInitialField) {
      unregister(`${name}.file`);
      setValue(name, defaultValueShape, { shouldDirty: false });
    }
  }, [isSubmitted]);

  return (
    <>
      <React.Fragment>
        {fieldsArray.map((field, index) => {
          const fieldPrefix = `${name}.${index}` as const;
          const isOnlyElement = fieldsArray.length === 1;
          const labelWithNumber = `${label} ${index > 0 ? index + 1 : ''}`;
          const shouldHideDelete =
            (isOnlyElement && !shouldHideInitialField) || hideDelete;

          return (
            <DocumentSection
              mt={mt}
              key={field.id}
              documentName={fieldPrefix}
              isDocumentRequired={isDocumentRequired}
              label={labelWithNumber}
              handleRemove={
                shouldHideDelete ? undefined : () => removeField(index)
              }
              hideDelete={hideDelete}
              isReadonly={isReadonly}
              helperText={helperText}
              defaultReadOnlyName={defaultReadOnlyName}
            />
          );
        })}

        <GridItem colSpan={{ lg: 2 }}>
          <Center w="full">
            <Button
              onClick={() => appendField(defaultValueShape)}
              variant="linkPrimary"
              size="sm"
              leftIcon={<FiPlus />}
              isDisabled={isReadonly}
            >
              {additionalLabel}
            </Button>
          </Center>
        </GridItem>
      </React.Fragment>
    </>
  );
};
